import { queryOptions, keepPreviousData } from '@tanstack/react-query'
import { apiClient } from '../client'
import type { DashboardStats, PaginatedResponse, Agent, RecentAgent, AgentVersion, KnowledgePackLink, AccessPolicy } from '../types'

export interface AgentListParams {
  search?: string
  category?: string
  status?: string
  brand_id?: string
  page?: number
  page_size?: number
  sort_by?: string
  sort_order?: string
}

export const agentQueries = {
  dashboard: () =>
    queryOptions({
      queryKey: ['agents', 'dashboard'],
      queryFn: () => apiClient<DashboardStats>('/api/v1/agents/dashboard'),
      staleTime: 1000 * 60 * 2, // 2 minutes
    }),

  recent: (limit = 10) =>
    queryOptions({
      queryKey: ['agents', 'recent', { limit }],
      queryFn: () => apiClient<RecentAgent[]>(`/api/v1/agents/recent?limit=${limit}`),
      staleTime: 1000 * 60 * 2, // 2 minutes
    }),

  list: (params: AgentListParams = {}) =>
    queryOptions({
      queryKey: ['agents', 'list', params],
      queryFn: () => {
        const searchParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            searchParams.set(key, String(value))
          }
        })
        return apiClient<PaginatedResponse<Agent>>(
          `/api/v1/agents?${searchParams.toString()}`,
        )
      },
      placeholderData: keepPreviousData,
      staleTime: 1000 * 30, // 30 seconds for list
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: ['agents', 'detail', id],
      queryFn: () => apiClient<Agent>(`/api/v1/agents/${id}`),
      staleTime: 1000 * 60 * 2, // 2 minutes
    }),

  versions: (id: string) =>
    queryOptions({
      queryKey: ['agents', 'versions', id],
      queryFn: () => apiClient<AgentVersion[]>(`/api/v1/agents/${id}/versions`),
      staleTime: 1000 * 60 * 5, // 5 minutes
    }),

  knowledgePacks: (id: string) =>
    queryOptions({
      queryKey: ['agents', 'knowledge-packs', id],
      queryFn: () => apiClient<KnowledgePackLink[]>(`/api/v1/agents/${id}/knowledge-packs`),
      staleTime: 1000 * 60 * 5,
    }),

  accessPolicy: (id: string) =>
    queryOptions({
      queryKey: ['agents', 'access-policy', id],
      queryFn: () => apiClient<AccessPolicy[]>(`/api/v1/access-policies?agent_id=${id}`),
      staleTime: 1000 * 60 * 5,
    }),
}
